import { useState, useEffect } from "react";
import { useLazyQuery, useMutation } from "@apollo/client";
import { toast } from "react-toastify";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDCircularLoader from "components/MDCircularLoader";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import PageShell from "components/common/PageShell";
import ListEmptyState from "components/common/ListEmptyState";
import ConfirmModal from "components/common/ConfirmModal";
import DataTable from "../pr-management/components/DataTable";
import { makeColumns } from "./components/makeColumns";
import ProfileDetailModal from "./components/ProfileDetailModal";
import SendMessageModal from "./components/SendMessageModal";
import {
  FILTER_PROFILES_PAGINATED,
  IFilterProfiles,
  IFilterProfilesVars,
  IProfileRow,
} from "graphql/queries/filterProfilesPaginated";
import { SEND_PROFILE_MESSAGE } from "graphql/mutations/sendProfileMessage";
import { ADMIN_RELEASE_PROFILE } from "graphql/mutations/adminReleaseProfile";
import { ADMIN_DELETE_PROFILE } from "graphql/mutations/adminDeleteProfile";

const CHANNEL_OPTIONS = ["WHATSAPP", "TELEGRAM", "WEB"];

function Profiles(): JSX.Element {
  const [tableData, setData] = useState<any>({ rows: [], columns: [] });
  const [paginationInfo, setPaginationInfo] = useState<any>({});
  const [pageSize, setSize] = useState<number>(10);
  const [filters, setFilters] = useState<any>({});
  const [searchInput, setSearchInput] = useState<string>("");
  const [loader, setLoader] = useState<boolean>(false);

  const [detail, setDetail] = useState<IProfileRow | null>(null);
  const [messageProfile, setMessageProfile] = useState<IProfileRow | null>(null);
  const [confirm, setConfirm] = useState<{ type: "disconnect" | "delete"; row: IProfileRow } | null>(
    null
  );

  const [getProfiles, { data, loading }] = useLazyQuery<IFilterProfiles, IFilterProfilesVars>(
    FILTER_PROFILES_PAGINATED,
    {
      fetchPolicy: "network-only",
      onCompleted: (res) => setPaginationInfo(res?.filterProfilesPaginated?.paginationInfo),
      onError: (err) => toast.error(err?.message || "Failed to load profiles"),
    }
  );

  const [sendMessage] = useMutation(SEND_PROFILE_MESSAGE);
  const [releaseProfile] = useMutation(ADMIN_RELEASE_PROFILE);
  const [deleteProfile] = useMutation(ADMIN_DELETE_PROFILE);

  const fetchPage = (currentPage = 0) => {
    getProfiles({
      variables: {
        paginationInput: { currentPage, perPage: pageSize },
        filterInput: filters,
      },
    });
  };

  useEffect(() => {
    fetchPage(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, pageSize]);

  useEffect(() => {
    const rows = data?.filterProfilesPaginated?.data || [];
    setData({ rows, columns: makeColumns(handleAction) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const handleFilter = (key: string, value: any) => {
    setFilters((f: any) => {
      const next = { ...f };
      if (value === "" || value === undefined || value === null) delete next[key];
      else next[key] = value;
      return next;
    });
  };

  const handleAction = (id: string, action: string, row?: IProfileRow) => {
    if (action === "view") setDetail(row || null);
    else if (action === "message") setMessageProfile(row || null);
    else if (action === "disconnect" && row) setConfirm({ type: "disconnect", row });
    else if (action === "delete" && row) setConfirm({ type: "delete", row });
  };

  const handleSend = (message: string) => {
    if (!messageProfile) return;
    setLoader(true);
    sendMessage({ variables: { profileId: messageProfile.id, message } })
      .then((res) => {
        setLoader(false);
        setMessageProfile(null);
        if (res?.data?.sendProfileMessage?.delivered) toast.success("Message sent");
        else toast.warning("Message not delivered (channel offline or notifications disabled)");
      })
      .catch((err) => {
        setLoader(false);
        toast.error(err?.message || "Failed to send message");
      });
  };

  const handleConfirm = () => {
    if (!confirm) return;
    const { type, row } = confirm;
    const mutation = type === "disconnect" ? releaseProfile : deleteProfile;
    setLoader(true);
    mutation({ variables: { profileId: row.id } })
      .then(() => {
        setLoader(false);
        toast.success(type === "disconnect" ? "Profile disconnected" : "Profile deleted");
        fetchPage(paginationInfo?.currentPage || 0);
      })
      .catch((err) => {
        setLoader(false);
        toast.error(err?.message || "Action failed");
      });
  };

  const filtersRow = (
    <MDBox mb={2}>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} sm={4} md={3}>
          <MDInput
            select
            fullWidth
            label="Channel"
            value={filters.channel || ""}
            onChange={(e: any) => handleFilter("channel", e.target.value)}
          >
            <MenuItem value="">All channels</MenuItem>
            {CHANNEL_OPTIONS.map((c) => (
              <MenuItem key={c} value={c}>
                {c}
              </MenuItem>
            ))}
          </MDInput>
        </Grid>
        <Grid item xs={12} sm={4} md={3}>
          <MDInput
            select
            fullWidth
            label="Connection"
            value={filters.connected === undefined ? "" : filters.connected ? "yes" : "no"}
            onChange={(e: any) =>
              handleFilter(
                "connected",
                e.target.value === "" ? undefined : e.target.value === "yes"
              )
            }
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="yes">Connected</MenuItem>
            <MenuItem value="no">Not connected</MenuItem>
          </MDInput>
        </Grid>
        <Grid item xs={12} sm={4} md={4}>
          <MDInput
            fullWidth
            label="Search name or channel user"
            value={searchInput}
            onChange={(e: any) => setSearchInput(e.target.value)}
            onKeyDown={(e: any) => {
              if (e.key === "Enter") handleFilter("search", searchInput.trim() || undefined);
            }}
          />
        </Grid>
      </Grid>
    </MDBox>
  );

  return (
    <>
      {loader && <MDCircularLoader overlayloader startLoader />}
      <DashboardNavbar />
      <PageShell filters={filtersRow}>
        {loading ? (
          <MDCircularLoader startLoader />
        ) : data?.filterProfilesPaginated?.data?.length ? (
          <DataTable
            table={tableData}
            showHeader
            loading={loading}
            paginationInfo={paginationInfo || {}}
            handlePagination={(page: number) => fetchPage(page)}
            entriesPerPage={{ defaultValue: pageSize }}
            setSize={setSize}
          />
        ) : (
          <ListEmptyState message="No profiles found" />
        )}
      </PageShell>

      {detail && <ProfileDetailModal profile={detail} onClose={() => setDetail(null)} />}
      {messageProfile && (
        <SendMessageModal
          profile={messageProfile}
          busy={loader}
          onClose={() => setMessageProfile(null)}
          onSend={handleSend}
        />
      )}
      {confirm && (
        <ConfirmModal
          title={confirm.type === "disconnect" ? "Disconnect profile" : "Delete profile"}
          message={
            confirm.type === "disconnect"
              ? "This will detach the profile from its 01wire account. Continue?"
              : "Are you sure you want to delete this profile?"
          }
          confirmLabel={confirm.type === "disconnect" ? "Disconnect" : "Delete"}
          confirmColor={confirm.type === "disconnect" ? "warning" : "error"}
          onConfirm={handleConfirm}
          onClose={() => setConfirm(null)}
        />
      )}
    </>
  );
}

export default Profiles;
